import { Component, OnInit } from '@angular/core'
import { ToastrService } from 'ngx-toastr'
import { CommonModule } from '@angular/common'
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormArray,
  FormsModule,
} from '@angular/forms'
import { QuotationService } from '../quotation.service'
import { Router } from '@angular/router'
import { MasterCommonServices } from '../master.common.service'
import { MemberModel } from '../../member-client/member.model'
import { MasterDatabaseDisplayModel } from '../../../shared/master-database-display.model'
import { UnitModel } from '../../masters/unit.master/unit.model'
import { MatDialog } from '@angular/material/dialog'
import { MasterAddUpdateDialogComponent } from '../../../shared/components/master-add-update-dialog/master-add-update-dialog.component'
import { UnitService } from '../../masters/unit.master/unit.service'
import { UserModel } from '../../users/user_model'

@Component({
  selector: 'app-quotation',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './addQuotation.component.html',
  styleUrls: ['./addQuotation.component.css'],
})
export class AddQuotationComponent implements OnInit {
  isLoading: boolean = false
  submitted: boolean = false
  isToastrShown: boolean = false
  quotationForm: FormGroup = new FormGroup({})
  currentPriority: number = 0
  companyMembersList: MemberModel[] = []
  mode: string = 'add'
  title: string = 'Add New Quotation'
  quotationData: any
  permissions: string[] = []
  quotationUsers: UserModel[] = []
  unitColumns: MasterDatabaseDisplayModel[] = [
    {
      key: 'unitName',
      displayName: 'Unit Name',
      hiddenDataTable: false,
      hiddenDialog: false,
      type: 'text',
    },
    {
      key: 'id',
      displayName: 'Id',
      hiddenDataTable: true,
      hiddenDialog: true,
      type: 'text',
    },
  ]

  constructor(
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    public quotationCommonServices: MasterCommonServices,
    private quotationService: QuotationService,
    private unitService: UnitService,
    private router: Router,
    private dialog: MatDialog,
  ) {
    this.initializeForm()
    const navigation = this.router.getCurrentNavigation()
    if (navigation?.extras.state) {
      const params = navigation.extras.state

      this.mode = params['mode'] || 'add'
      if (this.mode !== 'add') {
        this.quotationData = {
          quotationFields: JSON.parse(params['quotationFields']),
          quotationItems: JSON.parse(params['quotationItems']),
          quotationTerms: JSON.parse(params['quotationTerms']),
        }
        this.populateForm(this.quotationData)
        this.title = 'Update Quotation'
      }
    }
  }
  ngOnInit(): void {
    this.setUpMasters()
  }

  async setUpMasters(): Promise<void> {
    if (!this.quotationCommonServices.isDataLoaded) {
      await this.quotationCommonServices.getAllMasters()
    }
    this.quotationUsers = this.quotationCommonServices.usersList.filter(
      (user) => user.userPermissions?.includes('QuotationsAccess'),
    )
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.resizeAllTextareas())
  }

  resizeAllTextareas(): void {
    const textareas = document.querySelectorAll('textarea.auto-resize')
    textareas.forEach((textarea: any) => {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    })
  }

  autoResize(event: any) {
    event.target.style.height = 'auto'
    event.target.style.height = event.target.scrollHeight + 'px'
  }

  openAddUpdateUnitDialog(): void {
    const mode = 'add'
    const fields = this.unitColumns
    const dialogRef = this.dialog.open(MasterAddUpdateDialogComponent, {
      data: { item: {}, mode, fields },
    })

    dialogRef.afterClosed().subscribe((result) => {
      this.addUnit(result)
    })
  }

  addUnit(unit: any): void {
    this.unitService.addUnit(unit).subscribe({
      next: () => {
        this.toastr.success('Unit Added Successfully!', 'Success')
        this.unitService.getAllUnits().subscribe({
          next: (data: any) => {
            this.quotationCommonServices.unitsList = data
          },
          error: (error: any) => {
            this.toastr.error(error.statusText, 'Error', {
              timeOut: 15000,
              closeButton: true,
              progressBar: true,
            })
          },
        })
      },
      error: (error: any) => {
        this.toastr.error(error.statusText, 'Error', {
          timeOut: 15000,
          closeButton: true,
          progressBar: true,
        })
      },
    })
  }

  populateForm(data: any): void {

    this.quotationForm
      .get('quotationFields')
      ?.get('quotationAttentionId')
      ?.setValue(null)
    this.quotationForm.patchValue({
      quotationFields: {
        ...data.quotationFields,
        quotationDate: data.quotationFields.quotationDate.split('T')[0],
        quotationFollowUpDate: data.quotationFields.quotationFollowUpDate?.split(
          'T',
        )[0],
      },
      quotationTerms: data.quotationTerms,
    })
    this.onCompanyChange()
    this.quotationForm
      .get('quotationFields')
      ?.get('quotationAttentionId')
      ?.setValue(data.quotationFields.quotationAttentionId.toString())
    if (data.quotationItems?.length > 0) {
      const itemsFormArray = this.quotationForm.get(
        'quotationItems',
      ) as FormArray
      itemsFormArray?.clear()

      data.quotationItems.forEach((item: any) => {
        const itemGroup = this.formBuilder.group({
          id: [item.id],
          srNo: [item.srNo],
          quotationId: data.quotationFields.id,
          description: [item.description, Validators.required],
          comments: [item.comments || ''],
          quantity: [item.quantity, Validators.required],
          unit: [item.unit, Validators.required],
          cost: [item.cost],
          margin: [item.margin],
          isDeleted: [item.isDeleted],
          packagingCharges: [item.packagingCharges],
          unitPrice: [item.unitPrice],
          manualUnitPrice: [item.manualUnitPrice ?? false],
          totalPrice: [{ value: item.totalPrice, disabled: true }],
        })
        itemsFormArray?.push(itemGroup)
      })
    }
    const dateFormatted = data['quotationFields']['quotationDate'].split('T')[0]
  }

  initializeForm(): void {
    this.quotationForm = this.formBuilder.group({
      quotationFields: this.createQuotationFields(),
      quotationItems: this.formBuilder.array([]),
      quotationTerms: this.createQuotationTerms(),
    })
  }

  createQuotationFields(): FormGroup {
    return this.formBuilder.group({
      quotationMadeById: [null, Validators.required],
      quotationAssignedToId: [null, Validators.required],
      id: [{ value: null, disabled: true }],
      quotationId: [{ value: null, disabled: true }],
      quotationDate: [
        new Date().toISOString().substring(0, 10),
        Validators.required,
      ],
      quotationFollowUpDate: [null],
      quotationStage: [null, Validators.required],
      quotationCompanyId: [null, Validators.required],
      quotationAttentionId: [null, Validators.required],
      reference: [null],
      quotationImportance: [null, Validators.required],
      quotationPriority: [0, Validators.required],
      quotationStatus: ['open'],
      netTotal: [{ value: null, disabled: true }],
      discountType: [null],
      discount: [null],
      gstAmount: [{ value: null, disabled: true }],
      otherCharges: [null],
      grandTotal: [{ value: null, disabled: true }],
      isGstRemoved: [false],
      isRoundOff: [false],
    })
  }

  createQuotationTerms(): FormGroup {
    return this.formBuilder.group({
      id: [null],
      quotationId: [null],
      deliveryNameId: [null],
      currencyId: [null],
      deliveryTimeId: [null],
      countryofOriginId: [null],
      paymentId: [null],
      mtcTypeId: [null],
      validityId: [null],
      packingTypeId: [null],
    })
  }

  showToastrError(message: string) {
    if (!this.isToastrShown) {
      this.isToastrShown = true
      this.toastr.error(message, 'Error').onHidden.subscribe(() => {
        this.isToastrShown = false
      })
    }
  }

  onPriorityChange(event: Event) {
    this.quotationForm.markAsDirty()
    this.currentPriority = +(event.target as HTMLInputElement).value
  }

  onToggleRemoveGst(): void {
    this.quotationForm.markAsDirty()
    this.calculateGrandTotal()
  }

  onQuantityChanged(index: number): void {
    this.quotationForm.markAsDirty()
    this.updateQuotationItemTotals(index, false)
    this.calculateNetTotal()
    this.calculateGrandTotal()
  }

  onCostRelatedFieldChanged(index: number): void {
    this.quotationForm.markAsDirty()
    const itemGroup = this.quotationItems.at(index) as FormGroup
    itemGroup.get('manualUnitPrice')?.setValue(false, { emitEvent: false })
    this.updateQuotationItemTotals(index, true)
    this.calculateNetTotal()
    this.calculateGrandTotal()
  }

  onUnitPriceChanged(index: number): void {
    this.quotationForm.markAsDirty()
    const itemGroup = this.quotationItems.at(index) as FormGroup
    itemGroup.get('manualUnitPrice')?.setValue(true, { emitEvent: false })
    this.updateQuotationItemTotals(index, false)
    this.calculateNetTotal()
    this.calculateGrandTotal()
  }

  private updateQuotationItemTotals(
    index: number,
    recalcUnitPriceFromCost: boolean,
  ): void {
    const itemGroup = this.quotationItems.at(index) as FormGroup
    const qty = Number(itemGroup.get('quantity')?.value) || 0
    const manualUnitPrice = itemGroup.get('manualUnitPrice')?.value === true
    let unitPrice = this.parseNumericControlValue(
      itemGroup.get('unitPrice')?.value,
    )

    if (!manualUnitPrice && recalcUnitPriceFromCost) {
      const costValue = itemGroup.get('cost')?.value
      const cost = this.parseNumericControlValue(costValue)

      if (cost === null) {
        itemGroup.get('unitPrice')?.setValue(null, { emitEvent: false })
        itemGroup.get('totalPrice')?.setValue(null, { emitEvent: false })
        return
      }

      unitPrice = cost
      const margin = Number(itemGroup.get('margin')?.value ?? 0) || 0
      const packagingCharges =
        Number(itemGroup.get('packagingCharges')?.value ?? 0) || 0

      unitPrice += (margin / 100) * unitPrice
      unitPrice += (packagingCharges / 100) * unitPrice

      itemGroup.get('unitPrice')?.setValue(unitPrice.toFixed(2), {
        emitEvent: false,
      })
    }

    if (unitPrice === null) {
      itemGroup.get('totalPrice')?.setValue(null, { emitEvent: false })
      return
    }

    const totalPrice = qty * unitPrice
    itemGroup.get('totalPrice')?.setValue(totalPrice, { emitEvent: false })
  }

  calculateNetTotal(): void {
    let netTotal: number = 0

    this.quotationItems.controls.forEach((itemGroup) => {
      const isDeleted = itemGroup.get('isDeleted')?.value
      if (!isDeleted) {
        const totalPrice =
          Number.parseFloat(itemGroup.get('totalPrice')?.value) || 0
        netTotal += totalPrice
      }
    })
    this.quotationForm
      .get('quotationFields')
      ?.get('netTotal')
      ?.setValue(netTotal)
  }

  calculateGrandTotal(): void {
    const quotationFieldGroup = this.quotationForm.get('quotationFields')
    const netTotal = +quotationFieldGroup?.get('netTotal')?.value || 0
    const discountType = quotationFieldGroup?.get('discountType')?.value
    const discountValue = +quotationFieldGroup?.get('discount')?.value || 0
    const pAndf = +quotationFieldGroup?.get('otherCharges')?.value || 0

    let discountAmount = 0
    if (discountType === 'percent') {
      discountAmount = (discountValue / 100) * netTotal
    } else if (discountType === 'value') {
      discountAmount = discountValue
    }

    const discountedAmount = netTotal - discountAmount

    let gstAmount = 0
    const isGstRemoved = this.quotationForm.get('quotationFields.isGstRemoved')
      ?.value
    if (!isGstRemoved) {
      const gstPercent = 18 / 100
      const discountGst = discountedAmount * gstPercent
      const pAndfGst = pAndf * gstPercent
      gstAmount = discountGst + pAndfGst
    }

    quotationFieldGroup?.get('gstAmount')?.setValue(gstAmount.toFixed(2))

    let grandTotal = discountedAmount + pAndf + gstAmount
    const isRoundOff = this.quotationForm.get('quotationFields.isRoundOff')
      ?.value
    if (isRoundOff) {
      grandTotal = Math.round(grandTotal)
    }

    quotationFieldGroup?.get('grandTotal')?.setValue(grandTotal.toFixed(2))
  }

  toggleRoundOff(event: Event): void {
    this.quotationForm.markAsDirty()
    const isChecked = (event.target as HTMLInputElement).checked
    const grandTotalControl = this.quotationForm
      .get('quotationFields')
      ?.get('grandTotal')

    if (isChecked) {
      const roundedValue = Math.round(grandTotalControl?.value || 0)
      grandTotalControl?.setValue(roundedValue)
    } else {
      this.calculateGrandTotal()
    }
  }

  get quotationItems(): FormArray {
    return this.quotationForm.get('quotationItems') as FormArray
  }

  addRow() {
    this.quotationForm.markAsDirty()
    const itemGroup = this.formBuilder.group({
      id: [null],
      srNo: [null],
      description: [null, Validators.required],
      comments: [null],
      quantity: [null, Validators.required],
      unit: [null, Validators.required],
      cost: [null],
      margin: [null],
      isDeleted: [false],
      packagingCharges: [null],
      unitPrice: [null],
      manualUnitPrice: [false],
      totalPrice: [{ value: null, disabled: true }],
    })
    this.quotationItems.push(itemGroup)
  }

  removeRow(index: number): void {
    this.quotationForm.markAsDirty()
    const formArray = this.quotationItems as FormArray
    const formGroup = formArray.at(index) as FormGroup
    formGroup.patchValue({ isDeleted: true })
    Object.keys(formGroup.controls).forEach((key) => {
      formGroup.get(key)?.clearValidators()
      formGroup.get(key)?.updateValueAndValidity()
    })
    this.calculateNetTotal()
    this.calculateGrandTotal()

  }

  hasVisibleRows(): boolean {
    return this.quotationItems.controls.some(
      (item) => !item.get('isDeleted')?.value,
    )
  }

  submit() {
    this.submitted = true

    if (this.quotationForm.invalid && this.hasVisibleRows()) {

      this.toastr.error('Please fill all the required fields', 'Error')
      return
    }

    if (this.hasVisibleRows() == false) {
      this.toastr.error('Please add at least one item', 'Error')
      return
    }

    const quotationData = this.quotationForm.getRawValue()

    const processedData = this.processFormData(quotationData)
    const isGstRemoved = this.quotationForm.get('quotationFields.isGstRemoved')
      ?.value
    if (!isGstRemoved) {
      processedData.gstPercent = 18
    } else {
      processedData.gstPercent = 0
    }
    if (this.mode === 'add' && this.quotationForm.get('quotationFields')?.get('quotationId')?.value === null) {
      this.quotationService.addQuotation(processedData).subscribe({
        next: (data) => {

          if (data.id) {
            this.quotationService.getQuotationById(data.id).subscribe({
              next: (quotation) => {
                this.populateForm(quotation)
              },
              error: (error) => {
                this.toastr.error(error.statusText, 'Error', {
                  timeOut: 15000,
                  closeButton: true,
                  progressBar: true,
                })
              },
            })
          }
          this.quotationForm.markAsPristine()
          this.title = 'Update Quotation'
          this.toastr.success('Quotation Added Successfully!', 'Success')
        },
        error: (error: any) => {


          this.toastr.error(error.statusText, 'Error', {
            timeOut: 15000,
            closeButton: true,
            progressBar: true,
          })
        },
      })
    } else {
      this.quotationService.updateQuotation(processedData).subscribe({
        next: (data) => {
          if (data.id) {
            this.quotationService.getQuotationById(data.id).subscribe({
              next: (quotation) => {
                this.populateForm(quotation)
              },
              error: (error) => {
                this.toastr.error(error.statusText, 'Error', {
                  timeOut: 15000,
                  closeButton: true,
                  progressBar: true,
                })
              },
            })
          }
          this.quotationForm.markAsPristine()
          this.toastr.success('Quotation Updated Successfully!', 'Success')
        },
        error: (error: any) => {
          this.toastr.error(error.statusText, 'Error', {
            timeOut: 15000,
            closeButton: true,
            progressBar: true,
          })
        },
      })
    }
  }

  processFormData(data: any): any {
    const dateTimeNow = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    ).toISOString()

    if (this.mode === 'add') {
      data.quotationFields.addedOn = dateTimeNow
      data.quotationTerms.addedOn = dateTimeNow
    } else {
      data.quotationFields.addedOn = this.quotationData.quotationFields.addedOn
      data.quotationTerms.addedOn = this.quotationData.quotationTerms.addedOn
    }
    data.quotationFields.modifiedOn = dateTimeNow
    data.quotationTerms.modifiedOn = dateTimeNow
    this.processFields(data.quotationFields)
    if (Array.isArray(data.quotationItems)) {
      data.quotationItems = data.quotationItems
        .filter((item: any) => this.mode === 'update' || !item.isDeleted) // Remove deleted items
        .map((item: any, index: number) => {
          item.srNo = index + 1
          item.addedOn = dateTimeNow
          item.modifiedOn = dateTimeNow
          this.processFields(item)
          return item
        })
    }
    Object.keys(data.quotationTerms).forEach((key) => {
      if (data.quotationTerms[key] === 'null') {
        data.quotationTerms[key] = null
      }
    })

    return data
  }

  processFields(fields: any): void {
    for (const key in fields) {
      if (fields.hasOwnProperty(key)) {
        const value = fields[key]

        if (value === '') {
          if (key === 'unitPrice') {
            fields[key] = null
          } else if (this.isNumericField(key)) {
            fields[key] = 0
          }
        }

        if ((value === null || value === undefined) && this.isNumericField(key)) {
          if (key !== 'unitPrice') {
            fields[key] = 0
          }
        }
      }
    }
  }

  isNumericField(key: string): boolean {
    const numericFields = [
      'discount',
      'otherCharges',
      'totalPrice',
      'grandTotal',
      'netTotal',
      'gstAmount',
      'quantity',
      'cost',
      'margin',
      'packagingCharges',
    ]
    return numericFields.includes(key)
  }

  getTotalPriceDisplay(itemGroup: AbstractControl): string {
    const unitPrice = this.parseNumericControlValue(
      itemGroup.get('unitPrice')?.value,
    )
    if (unitPrice === null) {
      return ''
    }
    if (unitPrice === 0) {
      return 'REGRET'
    }
    const totalPriceValue = itemGroup.get('totalPrice')?.value
    return totalPriceValue === null || totalPriceValue === undefined
      ? ''
      : Number(totalPriceValue).toFixed(2)
  }

  private parseNumericControlValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null
    }
    const numberValue = Number(value)
    return Number.isNaN(numberValue) ? null : numberValue
  }

  getSerialNumber(index: number): number {
    let count = 1
    for (let i = 0; i < index; i++) {
      if (!this.quotationItems.at(i).get('isDeleted')?.value) {
        count++
      }
    }
    return count
  }

  onCompanyChange() {
    this.quotationForm.markAsDirty()
    const selectedCompanyId = this.quotationForm
      .get('quotationFields')
      ?.get('quotationCompanyId')
      ?.value.toString()
    this.companyMembersList = this.quotationCommonServices.membersList.filter(
      (member) => member.clientId.toString() === selectedCompanyId,
    )
    if (this.companyMembersList.length > 0) {
      this.quotationForm
        .get('quotationFields')
        ?.get('quotationAttentionId')
        ?.setValue(this.companyMembersList[0].id)
    }
  }
}
